import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  Firestore,
  getDocs,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { Group, GroupType } from '../../../models/group.model';
import { MembershipStatus } from '../../../models/membership.status';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { LoadingDotsComponent } from '../../components/spinners/loading-dots/loading-dots.component';
import { DlgViewProfileComponent } from '../../profile/dlg-view-profile/dlg-view-profile.component';
import { DlgViewNoticeComponent } from '../notices/dlg-view-notice/dlg-view-notice.component';
import { DlgCreateCalendarEventComponent } from './dlg-create-event/dlg-create-event.component';

import * as groupActions from '../../../state/actions/group.actions';

import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'app-events-calendar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MomentPipe,
    BtnBackComponent,
    BtnActionComponent,
    LoadingDotsComponent,
    FormsModule,
    DlgCreateCalendarEventComponent,
  ],
  templateUrl: './events-calendar.component.html',
  styleUrl: './events-calendar.component.css',
})
export class EventsCalendarComponent implements OnInit {
  private readonly afs: Firestore = inject(Firestore);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly dialog: MatDialog = inject(MatDialog);
  private logger: NGXLogger = inject(NGXLogger);
  private readonly store: Store<AppState> = inject(Store<AppState>);
  private readonly toastr: ToastrService = inject(ToastrService);

  groupId: string | null = null;
  loading: boolean = false;

  selectedDate = moment();
  selectedMonth = moment().month() + 1; // 1-indexed (1-12)
  selectedYear = moment().year();
  daysInMonth: any[] = [];
  weeks: any[][] = [];
  events: any[] = [];
  selectedDayEvents: any[] = [];
  selectedDay: moment.Moment | null = null;
  loadingProfile = false;

  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  myRoles$: Observable<any> = new Observable();
  myRoles: any = null;
  group: Group | null | undefined = null;
  currentUser: any = null;
  membershipStatus = MembershipStatus;
  isMobile: boolean = window.innerWidth < 768;

  // Drawer / Event Detail properties
  selectedEvent: any = null;
  isEditing = false;
  editVolunteersRequired = 0;
  editVolunteerCriteria = '';

  memberStatus = MembershipStatus;

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  ngOnInit() {
    this.groupId = this.route.snapshot.paramMap.get('id');
    if (this.groupId) {
      this.store.dispatch(
        groupActions.select({
          groupId: this.groupId,
          source: 'events-calendar',
        }),
      );

      this.myRoles$ = this.store.select(
        groupSelector.getMyRolesInGroup(this.groupId),
      );
      this.myRoles$.subscribe((roles) => {
        this.myRoles = roles;
      });
      this.store.select(groupSelector.getSelectedGroup).subscribe((group) => {
        this.group = group;
      });
      this.store
        .select((state) => state.user.user)
        .subscribe((user) => {
          this.currentUser = user;
        });
      this.loadMonthEvents();
    }
  }

  prevMonth() {
    this.selectedDate.subtract(1, 'month');
    this.selectedMonth = this.selectedDate.month() + 1;
    this.selectedYear = this.selectedDate.year();
    this.loadMonthEvents();
  }

  nextMonth() {
    this.selectedDate.add(1, 'month');
    this.selectedMonth = this.selectedDate.month() + 1;
    this.selectedYear = this.selectedDate.year();
    this.loadMonthEvents();
  }

  selectDay(day: moment.Moment) {
    this.selectedDay = day;
    this.selectedDayEvents = this.getEventsForDate(day);
    this.selectedEvent = null;
    this.isEditing = false;
  }

  async loadMonthEvents() {
    if (!this.groupId) return;
    this.loading = true;

    try {
      const calendarRef = collection(
        this.afs,
        'groups',
        this.groupId,
        'events_calendar',
      );
      const q = query(calendarRef, where('month', '==', this.selectedMonth));
      const querySnapshot = await getDocs(q);

      this.events = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const rawDate = data['eventDate'] || data['date'];
        let parsedEventDate: Date | null = null;
        if (rawDate) {
          if (typeof rawDate.toDate === 'function') {
            parsedEventDate = rawDate.toDate();
          } else if (rawDate.seconds !== undefined) {
            parsedEventDate = new Date(rawDate.seconds * 1000);
          } else if (rawDate._seconds !== undefined) {
            parsedEventDate = new Date(rawDate._seconds * 1000);
          } else if (rawDate instanceof Date) {
            parsedEventDate = rawDate;
          } else {
            parsedEventDate = new Date(rawDate);
          }
        }
        return {
          id: doc.id,
          ...data,
          parsedEventDate,
        };
      }).filter((event: any) => event.type === 'notice' || event.type === 'birthday');

      this.logger.log('Loaded calendar events:', this.events);
      this.generateCalendar();
    } catch (e) {
      this.logger.error('Failed to load events calendar:', e);
    } finally {
      this.loading = false;
    }
  }

  generateCalendar() {
    const startOfMonth = moment(this.selectedDate).startOf('month');
    const startOfWeek = startOfMonth.clone().startOf('week');

    const endOfMonth = moment(this.selectedDate).endOf('month');
    const endOfWeek = endOfMonth.clone().endOf('week');

    const day = startOfWeek.clone();
    const days: any[] = [];

    // Ensure we render full weeks (at least 28 and up to 42 cells)
    while (day.isBefore(endOfWeek, 'day') || day.isSame(endOfWeek, 'day')) {
      const isCurrentMonth = day.month() === this.selectedDate.month();
      const isToday = day.isSame(moment(), 'day');
      const dayEvents = this.getEventsForDate(day);

      days.push({
        date: day.clone(),
        isCurrentMonth,
        isToday,
        eventsCount: dayEvents.length,
        events: dayEvents,
        dateStr: day.format('YYYY-MM-DD'),
      });
      day.add(1, 'day');
    }
    this.daysInMonth = days;

    const weeksList: any[][] = [];
    let currentWeek: any[] = [];
    days.forEach((d) => {
      currentWeek.push(d);
      if (currentWeek.length === 7) {
        weeksList.push(currentWeek);
        currentWeek = [];
      }
    });
    this.weeks = weeksList;

    // Default to select first day of month or today if we navigate to current month
    const today = moment();
    const isCurrentMonth =
      today.month() === this.selectedDate.month() &&
      today.year() === this.selectedDate.year();

    if (
      !this.selectedDay ||
      this.selectedDay.month() !== this.selectedDate.month() ||
      this.selectedDay.year() !== this.selectedDate.year()
    ) {
      if (isCurrentMonth) {
        this.selectDay(today);
      } else {
        this.selectDay(startOfMonth);
      }
    } else {
      this.selectDay(this.selectedDay);
    }
  }

  getEventsForDate(date: moment.Moment): any[] {
    const dayNum = date.date();
    const monthNum = date.month() + 1;
    const yearNum = date.year();

    return this.events.filter((event) => {
      if (event.type === 'birthday') {
        return event.month === monthNum && event.day === dayNum;
      } else if (event.type === 'notice') {
        // Direct match using stored day, month, year fields (timezone-independent)
        if (
          event.year !== undefined &&
          event.month !== undefined &&
          event.day !== undefined
        ) {
          return (
            event.year === yearNum &&
            event.month === monthNum &&
            event.day === dayNum
          );
        }
        // Fallback to parsing eventDate/date
        if (event.parsedEventDate) {
          const eventMoment = moment(event.parsedEventDate);
          return (
            eventMoment.date() === dayNum &&
            eventMoment.month() + 1 === monthNum &&
            eventMoment.year() === yearNum
          );
        }
      }
      return false;
    });
  }

  onBirthdayClick(event: any) {
    if (!this.groupId || !event.refId) return;

    try {
      // Construct a UserModel directly from the properties stored in the event document
      const userProfile: any = {
        uid: event.refId,
        name: event.name || 'Member',
        profession: event.profession || '',
        dob: event.dob || null,
        contact: event.contact || null,
        email: event.email || '',
        address: event.address || null,
        isDobViewable: event.showDob !== false,
        photoURL: event.photoURL || null,
      };

      this.dialog.open(DlgViewProfileComponent, {
        width: '500px',
        data: {
          user: userProfile,
          title: `${userProfile.name}'s Details`,
          description: 'Details of ' + userProfile.name,
        },
      });
    } catch (err) {
      this.logger.error('Failed to open member profile dialog:', err);
    }
  }

  onNoticeClick(event: any) {
    if (!this.groupId || !event.refId) return;

    this.dialog.open(DlgViewNoticeComponent, {
      width: '700px',
      maxHeight: '90vh',
      data: {
        groupId: this.groupId,
        noticeId: event.refId,
      },
    });
  }

  weekContainsSelectedDay(week: any[]): boolean {
    if (!this.selectedDay) return false;
    return week.some((day) => day.date.isSame(this.selectedDay, 'day'));
  }

  selectEvent(event: any) {
    this.selectedEvent = event;
    this.isEditing = false;
    this.editVolunteersRequired = event.volunteersRequired || 0;
    this.editVolunteerCriteria = event.volunteerCriteria || '';
  }

  closeDrawer() {
    this.selectedEvent = null;
    this.isEditing = false;
  }

  isAdminRole(roles?: any): boolean {
    const activeRoles = roles || this.myRoles;

    this.logger.debug(`Active Roles: ${activeRoles}`);
    if (this.group?.groupType === GroupType.church) {
      return activeRoles?.admin === true;
    }
    return activeRoles?.management === true;
  }

  async saveVolunteerRequirements(event: any) {
    if (!this.groupId) return;
    try {
      const docRef = doc(
        this.afs,
        'groups',
        this.groupId,
        'events_calendar',
        event.id,
      );
      await updateDoc(docRef, {
        volunteersRequired: this.editVolunteersRequired,
        volunteerCriteria: this.editVolunteerCriteria,
      });
      this.isEditing = false;
      this.toastr.success('Volunteer requirements updated successfully.');
      await this.loadMonthEvents();
      // Update local reference to keep drawer in sync
      const updated = this.events.find((e) => e.id === event.id);
      if (updated) {
        this.selectedEvent = updated;
      }
    } catch (err) {
      this.logger.error('Failed to update volunteer requirements:', err);
      this.toastr.error('Failed to update volunteer requirements.');
    }
  }

  async registerAsVolunteer(event: any) {
    if (!this.groupId || !this.currentUser) return;
    try {
      const docRef = doc(
        this.afs,
        'groups',
        this.groupId,
        'events_calendar',
        event.id,
      );
      const volunteer = {
        uid: this.currentUser.uid,
        name: this.currentUser.name || 'Member',
      };
      await updateDoc(docRef, {
        volunteers: arrayUnion(volunteer),
      });
      this.toastr.success('You have successfully registered as a volunteer!');
      await this.loadMonthEvents();
      // Update local reference to keep drawer in sync
      const updated = this.events.find((e) => e.id === event.id);
      if (updated) {
        this.selectedEvent = updated;
      }
    } catch (err) {
      this.logger.error('Failed to register as volunteer:', err);
      this.toastr.error('Failed to register.');
    }
  }

  async unregisterAsVolunteer(event: any) {
    if (!this.groupId || !this.currentUser) return;
    try {
      const docRef = doc(
        this.afs,
        'groups',
        this.groupId,
        'events_calendar',
        event.id,
      );
      // Find the volunteer entry matching current user uid
      const volunteerEntry = event.volunteers?.find(
        (v: any) => v.uid === this.currentUser.uid,
      );
      if (!volunteerEntry) return;

      await updateDoc(docRef, {
        volunteers: arrayRemove(volunteerEntry),
      });
      this.toastr.success('Volunteer registration cancelled.');
      await this.loadMonthEvents();
      // Update local reference to keep drawer in sync
      const updated = this.events.find((e) => e.id === event.id);
      if (updated) {
        this.selectedEvent = updated;
      }
    } catch (err) {
      this.logger.error('Failed to unregister as volunteer:', err);
      this.toastr.error('Failed to cancel registration.');
    }
  }

  isUserRegistered(event: any): boolean {
    if (!this.currentUser) return false;
    return (
      event.volunteers?.some((v: any) => v.uid === this.currentUser.uid) ||
      false
    );
  }

  onAddEvent() {
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      groupId: this.groupId,
    };
    dialogConfig.width = '550px';

    const dialogRef = this.dialog.open(
      DlgCreateCalendarEventComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadMonthEvents();
      }
    });
  }

  onEditEvent(event: any) {
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      groupId: this.groupId,
      event: event,
    };
    dialogConfig.width = '550px';

    const dialogRef = this.dialog.open(
      DlgCreateCalendarEventComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadMonthEvents().then(() => {
          if (this.selectedEvent) {
            const updated = this.events.find((e) => e.id === event.id);
            if (updated) {
              this.selectEvent(updated);
            } else {
              this.closeDrawer();
            }
          }
        });
      }
    });
  }
}
